'use client'

import { useState } from 'react'
import { motion } from 'motion/react'
import { Mail, ArrowRight, Check } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useLanguage } from '@/components/i18n/LanguageProvider'
import fr from '@/i18n/fr.json'
import en from '@/i18n/en.json'

export function ModernNewsletter() {
  const [email, setEmail] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(false)
  const { language } = useLanguage()
  const copy = language === 'fr' ? fr : en
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!email) return
    setIsSubmitted(true)
    toast.success(copy.newsletter.successTitle, {
      description: copy.newsletter.successDescription,
    })
    setTimeout(() => {
      setEmail('')
      setIsSubmitted(false)
    }, 3000)
  }

  return (
    <section id="contact" className="relative overflow-hidden bg-gradient-to-br from-primary via-primary/90 to-primary/75 py-24 dark:from-primary/85 dark:via-primary/75 dark:to-amber-500/60">
      <div className="absolute inset-0 opacity-15 dark:opacity-30">
        <div className="absolute left-1/4 top-0 h-96 w-96 rounded-full bg-primary-foreground/70 blur-3xl dark:bg-amber-100/45" />
        <div className="absolute bottom-0 right-1/4 h-96 w-96 rounded-full bg-primary-foreground/60 blur-3xl dark:bg-background/30" />
      </div>
      <div className="absolute inset-0 bg-black/5 dark:bg-white/5" />
      <div className="relative z-10 mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <motion.div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-foreground/16 backdrop-blur-sm dark:bg-primary-foreground/20" whileHover={{ scale: 1.05, rotate: 5 }} transition={{ duration: 0.3 }}>
            <Mail className="h-8 w-8 text-primary-foreground" />
          </motion.div>
          <h2 className="mb-6 text-4xl tracking-tight text-primary-foreground sm:text-5xl lg:text-6xl">{copy.newsletter.title}</h2>
          <p className="mx-auto mb-12 max-w-2xl text-xl text-primary-foreground/90 dark:text-primary-foreground/95">
            {copy.newsletter.subtitle}
          </p>

          <motion.form onSubmit={handleSubmit} className="mx-auto flex max-w-md flex-col gap-3 sm:flex-row" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }}>
            <Input
              type="email"
              placeholder={copy.newsletter.placeholder}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              className="newsletter-input h-14 rounded-full border-2 border-primary bg-primary-foreground/16 px-6 text-primary placeholder:text-primary focus:border-primary focus:bg-primary-foreground/24 dark:border-primary-foreground/35 dark:bg-primary-foreground/18 dark:text-white dark:placeholder:text-white/70"
            />
            <Button type="submit" size="lg" disabled={isSubmitted} className="h-14 rounded-full bg-primary-foreground px-8 text-primary hover:bg-primary-foreground/90">
              {isSubmitted ? (
                <>
                  <Check className="mr-2 h-5 w-5" />
                  {copy.newsletter.buttonDone}
                </>
              ) : (
                <>
                  {copy.newsletter.button}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </>
              )}
            </Button>
          </motion.form>
          <p className="mt-4 text-sm text-primary/80 dark:text-white">
            {copy.newsletter.privacyNote}
          </p>
        </motion.div>
      </div>
    </section>
  )
}
